<script setup lang="ts">
    import DataTableSelect from '@/components/datatable/DataTableSelect.vue';
    import type { DataTableSelectOption } from '@/types';
    import axios from 'axios';
    import { toast } from 'vue-sonner';
    import { ref, watch } from 'vue';
    import { useI18n } from 'vue-i18n';
    import { AcceptableValue } from 'reka-ui';

    const { t } = useI18n();

    import type { Row } from '@tanstack/vue-table';
    import type { DashboardTableData } from '@/types';

    const props = defineProps<{
        modelValue: AcceptableValue;
        companyId: number;
        row?: Row<DashboardTableData>;
    }>();

    const emit = defineEmits<{
        'update:modelValue': [value: AcceptableValue];
    }>();

    const currentValue = ref(props.modelValue);

    // Keep local model in sync if outer data changes (e.g., details hydration or other edits)
    watch(
        () => props.modelValue,
        (val) => {
            currentValue.value = val as AcceptableValue;
        },
    );

    const options: DataTableSelectOption[] = [
        {
            value: 0,
            label: t('forms.no'),
            color: 'red',
        },
        {
            value: 1,
            label: t('forms.yes'),
            color: 'green',
        },
        {
            value: 2,
            label: t('forms.loading'),
            color: 'yellow',
        },
    ];

    const handleChange = async (newValue: AcceptableValue) => {
        const previousValue = currentValue.value;

        try {
            const selectedOption = options.find((opt) => opt.value === newValue);
            if (!selectedOption) return;

            currentValue.value = newValue;

            await axios.post(route('v2.dashboard.reload-on-demand.update'), {
                company_id: props.companyId,
                reload_on_demand: newValue,
            });

            // Persist change into the row so virtualized/unloaded rows keep the updated value
            if (props.row && props.row.original) {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                (props.row.original as any).reload_on_demand = newValue as any;
            }

            emit('update:modelValue', newValue);
        } catch (error) {
            currentValue.value = previousValue;
            console.error('Failed to update status:', error);
            toast.error(t('dashboards.status_update_failed'));
        }
    };
</script>

<template>
    <DataTableSelect
        :model-value="currentValue"
        :options="options"
        @update:model-value="handleChange"
    />
</template>
